import { useCallback, useEffect, useState } from "react";
import { CalendarClock, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtUsd } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Toggle = ({ value, set, options, id }) => (
  <div className="grid grid-cols-2 gap-1 rounded-xl border border-[#212638] bg-[#0F111A] p-1">
    {options.map(([k, l]) => (
      <button key={k} onClick={() => set(k)} data-testid={`${id}-${k}`}
        className={`rounded-lg py-1.5 text-xs font-semibold transition-colors ${value === k ? "bg-[#F7931A] text-[#08090E]" : "text-[#94A3B8] hover:text-white"}`}>{l}</button>
    ))}
  </div>
);

const PlanRow = ({ p, onCancel }) => (
  <div className="flex items-center justify-between rounded-xl border border-[#212638] bg-[#0F111A] px-3 py-2.5" data-testid={`autobuy-plan-${p.plan_id}`}>
    <div>
      <p className="text-sm"><span className="font-mono font-semibold">{fmtUsd(p.usd_amount)}</span> <span className="text-[#94A3B8]">{p.frequency} · {p.method === "card" ? "card" : "balance"}</span></p>
      <p className="text-[11px] text-[#64748B]">
        {p.status === "pending_payment" ? "Awaiting card setup" : p.next_run_at ? `Next: ${new Date(p.next_run_at).toLocaleDateString()}` : ""}
        {p.last_status === "failed" && <span className="ml-2 text-red-400">Last run failed</span>}
      </p>
    </div>
    <button onClick={() => onCancel(p.plan_id)} data-testid={`autobuy-cancel-${p.plan_id}`} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-[#94A3B8] transition-colors hover:bg-red-500/10 hover:text-red-400"><X size={12} /> Cancel</button>
  </div>
);

export const AutoBuy = ({ onDone }) => {
  const { refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [amount, setAmount] = useState("10");
  const [frequency, setFrequency] = useState("weekly");
  const [method, setMethod] = useState("balance");
  const [busy, setBusy] = useState(false);
  const amt = parseFloat(amount) || 0;

  const load = useCallback(() => api.get("/autobuy").then((r) => setPlans(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/autobuy", { usd_amount: amt, frequency, method, origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(`Auto-buy started: ${fmtUsd(amt)} ${frequency}`);
      load(); refresh(); onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const cancel = (id) => api.delete(`/autobuy/${id}`).then(() => { toast.success("Auto-buy cancelled"); load(); }).catch((e) => toast.error(errMsg(e)));

  return (
    <div data-testid="autobuy-card">
      <div className="mb-4 flex items-center gap-2"><CalendarClock size={18} className="text-[#F7931A]" /><h3 className="text-lg font-semibold tracking-tight">Auto-buy</h3></div>
      <div className="space-y-3">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-[#64748B]">$</span>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" min="0" data-testid="autobuy-amount-input"
            className="w-full rounded-xl border border-[#212638] bg-[#0F111A] py-2.5 pl-7 pr-3 font-mono outline-none transition-colors focus:border-[#F7931A]" />
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[10, 25, 50, 100].map((a) => (
            <button key={a} onClick={() => setAmount(String(a))} data-testid={`autobuy-amount-${a}`}
              className={`rounded-lg border py-1.5 font-mono text-xs transition-colors ${Number(amount) === a ? "border-[#F7931A] bg-[#F7931A]/10 text-[#F7931A]" : "border-[#212638] text-[#94A3B8] hover:text-white"}`}>
              ${a}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Toggle value={frequency} set={setFrequency} id="autobuy-frequency" options={[["weekly", "Weekly"], ["monthly", "Monthly"]]} />
          <Toggle value={method} set={setMethod} id="autobuy-method" options={[["balance", "Balance"], ["card", "Card"]]} />
        </div>
        <p className="text-xs text-[#64748B]">{method === "card" ? "Your card is charged automatically each period via Stripe." : "Runs from your USD balance; the first buy happens now."} 1% fee applies (min $0.01).</p>
        <button onClick={create} disabled={busy || amt < 0.5 || amt > 10000} data-testid="autobuy-create-button"
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#F7931A] py-2.5 text-sm font-semibold text-[#F7931A] transition-colors hover:bg-[#F7931A] hover:text-[#08090E] disabled:opacity-50">
          {busy && <Loader2 size={14} className="animate-spin" />} {method === "card" ? "Set up with card" : "Start auto-buy"}
        </button>
        {amt > 0 && amt < 0.5 && <p className="text-xs text-red-400" data-testid="autobuy-error">Card processor minimum is $0.50</p>}
      </div>
      <div className="mt-4 space-y-2" data-testid="autobuy-plan-list">
        {plans.map((p) => <PlanRow key={p.plan_id} p={p} onCancel={cancel} />)}
      </div>
    </div>
  );
};
